import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Divider from '@material-ui/core/Divider';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import MuiList from '@material-ui/core/List/List';
import MuiListItem from '@material-ui/core/ListItem/ListItem';
import { useAnchorElement } from '@monorepo/shared/componentsV2/menus/useAnchorElement';
import React, {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { IconButton } from '../Button/IconButton';
import { VisuallyHidden } from '../VisuallyHidden';
import { MenuOption, MenuProps } from './Menu';
import { MenuPanelHeader } from './MenuPanelHeader';
import {
  IconContainer,
  MenuColor,
  menuListItem,
  PopperContainer,
  TitleContainer,
} from './styled';

const List = styled(MuiList)`
  padding-top: 0;
  padding-bottom: 0;
`;

const ListItem = styled(MuiListItem)<{
  width?: number;
  $hasMenuTitle?: boolean;
  $color: MenuColor;
}>`
  ${menuListItem}
`;

const BackButton = styled(IconButton)`
  min-width: 0;
  padding: 0.25rem;
  font-size: 1.25rem;
`;

const MenuRoot = styled.div`
  &:focus {
    outline: none;
  }
`;

export type TabbableMenuNestedContent = {
  title: string;
  content: ReactNode;
  headerAction?: ReactNode;
};

export type TabbableMenuOption = MenuOption & {
  nestedContent?: TabbableMenuNestedContent;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

type TabbableMenuProps = Omit<MenuProps, 'options'> & {
  menuFooter?: ReactNode;
  menuHeader?: ReactNode;
  options: TabbableMenuOption[];
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

export function TabbableMenu({
  buttonElement,
  className,
  color = 'primary',
  options,
  menuFooter,
  menuHeader,
  menuWidthShouldMatchButtonEl = false,
  menuTitle,
  popperPlacement = 'bottom-end',
  disablePopperPortal = false,
  onClose,
  onKeyDown,
}: TabbableMenuProps) {
  const [nestedOptionId, setNestedOptionId] = useState<string>();
  // Id of the row that opened the nested panel, so Back can return focus to it
  // instead of dropping focus to <body>.
  const returnFocusOptionIdRef = useRef<string>();

  const handleCloseMenu = useCallback(() => {
    setNestedOptionId(undefined);
    returnFocusOptionIdRef.current = undefined;
    onClose?.();
  }, [onClose]);

  const { anchorEl, handleAnchorElClick, handleClose, handleKeyDown } =
    useAnchorElement(handleCloseMenu);

  // add/override click handler on the button element to trigger opening the popper
  const buttonElementWithClickHandler = useMemo(
    () => React.cloneElement(buttonElement, { onClick: handleAnchorElClick }),
    [buttonElement, handleAnchorElClick],
  );

  const anchorElWidth = anchorEl?.offsetWidth || 0;
  const nestedOption = options.find(
    (option) => (option.id || option.displayName) === nestedOptionId,
  );
  const nestedContent = nestedOption?.nestedContent;

  // Callback refs rather than an effect: the popper is portaled, so its content
  // isn't in the DOM right away
  const focusOptionList = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const returnFocusOptionId = returnFocusOptionIdRef.current;
    returnFocusOptionIdRef.current = undefined;
    if (returnFocusOptionId) {
      // Escaped because the id falls back to displayName, which can contain
      // CSS-significant characters
      node
        .querySelector<HTMLElement>(
          `[data-menu-option-id="${CSS.escape(returnFocusOptionId)}"]`,
        )
        ?.focus();
      return;
    }
    // Focus the panel container (not a control) so Tab lands on the first
    // focusable child without drawing a focus ring on a title
    node.focus();
  }, []);

  const focusBackButton = useCallback(
    (node: HTMLButtonElement | null) => node?.focus(),
    [],
  );

  function renderNestedContent(content: TabbableMenuNestedContent) {
    return (
      <div
        role="presentation"
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
          handleKeyDown(event, { isTabNavigable: true })
        }
      >
        <MenuPanelHeader action={content.headerAction} title={content.title}>
          <BackButton
            color="default"
            onClick={() => {
              returnFocusOptionIdRef.current = nestedOptionId;
              setNestedOptionId(undefined);
            }}
            ref={focusBackButton}
            variant="text"
          >
            <VisuallyHidden>Back</VisuallyHidden>
            <ArrowBackIcon aria-hidden />
          </BackButton>
        </MenuPanelHeader>
        {content.content}
      </div>
    );
  }

  return (
    <>
      {buttonElementWithClickHandler}

      <PopperContainer
        disablePortal={disablePopperPortal}
        anchorEl={anchorEl}
        open={!!anchorEl}
        placement={popperPlacement}
        className={className}
        onKeyDown={onKeyDown}
      >
        {menuTitle && (
          <>
            <TitleContainer>{menuTitle}</TitleContainer>
            <Divider />
          </>
        )}
        <ClickAwayListener onClickAway={handleClose}>
          {nestedContent ? (
            renderNestedContent(nestedContent)
          ) : (
            <MenuRoot ref={focusOptionList} tabIndex={-1}>
              {menuHeader}
              <List>
                {options.map((opt) => (
                  <ListItem
                    button
                    key={opt.id || opt.displayName}
                    data-menu-option-id={opt.id || opt.displayName}
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                      if (opt.onClick) {
                        opt.onClick(event);
                      }
                      if (opt.nestedContent) {
                        setNestedOptionId(opt.id || opt.displayName);
                      }
                    }}
                    onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                      if (opt.onKeyDown) {
                        opt.onKeyDown(event);
                      }
                      handleKeyDown(event, { isTabNavigable: true });
                    }}
                    $color={opt.color || color}
                    $hasMenuTitle={!!menuTitle}
                    width={
                      menuWidthShouldMatchButtonEl ? anchorElWidth : undefined
                    }
                    style={opt.style}
                    divider={opt.divider}
                    disabled={opt.disabled}
                    disableRipple={opt.disabled}
                  >
                    {opt.icon && (
                      <IconContainer aria-hidden>{opt.icon}</IconContainer>
                    )}
                    {opt.displayName}
                    {opt.children}
                  </ListItem>
                ))}
              </List>
              {menuFooter}
            </MenuRoot>
          )}
        </ClickAwayListener>
      </PopperContainer>
    </>
  );
}
